import type { QualityMetrics } from "@/lib/types"

interface QualityPanelProps {
  quality: QualityMetrics
}

type Level = "good" | "warning" | "poor"

function level(pct: number): Level {
  if (pct >= 0.8) return "good"
  if (pct >= 0.5) return "warning"
  return "poor"
}

function barColor(l: Level): string {
  if (l === "good") return "bg-green-500"
  if (l === "warning") return "bg-amber-500"
  return "bg-red-400"
}

function statusText(l: Level): string {
  if (l === "good") return "Good"
  if (l === "warning") return "Needs work"
  return "Missing"
}

function statusColor(l: Level): string {
  if (l === "good") return "text-green-600"
  if (l === "warning") return "text-amber-600"
  return "text-red-500"
}

interface MetricRowProps {
  label: string
  detail: string
  pct: number
  lv: Level
}

function MetricRow({ label, detail, pct, lv }: MetricRowProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <span className={`text-xs font-medium ${statusColor(lv)}`}>
          {statusText(lv)}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted">
        <div
          className={`h-2 rounded-full transition-all ${barColor(lv)}`}
          style={{ width: `${Math.min(pct * 100, 100)}%` }}
        />
      </div>
      <p className="text-xs text-muted-foreground">{detail}</p>
    </div>
  )
}

export function QualityPanel({ quality: q }: QualityPanelProps) {
  const notesTotal = q.curator_notes.total
  const notesPct = notesTotal > 0 ? q.curator_notes.value / notesTotal : 0
  const notesLv = notesTotal === 0 ? ("poor" as Level) : level(notesPct)

  const timeTotal = q.time_context.total
  const timePct = timeTotal > 0 ? q.time_context.value / timeTotal : 0
  const timeLv = timeTotal === 0 ? ("poor" as Level) : level(timePct)

  const photosTotal = q.photos.total
  const photosPct = photosTotal > 0 ? q.photos.value / photosTotal : 0
  const photosLv = photosTotal === 0 ? ("poor" as Level) : level(photosPct)

  const hlVal = q.highlights.value
  const hlMin = q.highlights.target_min
  const hlMax = q.highlights.target_max
  const hlLv: Level =
    hlVal >= hlMin && hlVal <= hlMax ? "good" : hlVal < hlMin ? "poor" : "warning"
  const hlPct = hlMax > 0 ? Math.min(hlVal / hlMax, 1) : 0

  const staleLv: Level = q.stale.value === 0 ? "good" : "warning"
  const stalePct = q.stale.value === 0 ? 1 : 0

  const evLv: Level =
    q.events.value >= q.events.target_min
      ? "good"
      : q.events.value > 0
        ? "warning"
        : "poor"
  const evPct =
    q.events.target_min > 0 ? Math.min(q.events.value / q.events.target_min, 1) : 0

  return (
    <div className="space-y-4 p-1">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Guide quality
      </p>

      <MetricRow
        label="Curator notes"
        detail={`${q.curator_notes.value} of ${q.curator_notes.total} items have notes`}
        pct={notesPct}
        lv={notesLv}
      />

      <MetricRow
        label="Time context"
        detail={`${q.time_context.value} of ${q.time_context.total} items tagged`}
        pct={timePct}
        lv={timeLv}
      />

      <MetricRow
        label="Photos"
        detail={`${q.photos.value} of ${q.photos.total} items have a cover photo`}
        pct={photosPct}
        lv={photosLv}
      />

      <MetricRow
        label="Highlights"
        detail={`${hlVal} highlighted — target ${hlMin}–${hlMax}`}
        pct={hlPct}
        lv={hlLv}
      />

      <MetricRow
        label="Stale content"
        detail={
          q.stale.value === 0
            ? "0 stale POIs"
            : `${q.stale.value} stale POI${q.stale.value !== 1 ? "s" : ""} (not updated in 90 days)`
        }
        pct={stalePct}
        lv={staleLv}
      />

      <MetricRow
        label="Upcoming events"
        detail={`${q.events.value} events — minimum target ${q.events.target_min}`}
        pct={evPct}
        lv={evLv}
      />
    </div>
  )
}
